import { PHONE_DISPLAY, PHONE_TEL } from '../lib/constants'

const SECTIONS = [
  {
    n: 1,
    h: 'Scope of This Policy',
    paras: [
      'This Privacy Policy explains how True North Care and Support collects, uses, discloses, stores, and retains personal information in the course of providing home care services. This Policy applies to all employees, contractors, volunteers, and authorized representatives of True North Care and Support.',
    ],
  },
  {
    n: 2,
    h: 'The Types of Information We Collect',
    paras: ['To provide safe, effective, and customized home care, we collect two distinct categories of information:'],
    bullets: [
      {
        label: 'Personal Information (Governed by PIPA):',
        text: 'General identifying details including names, home addresses, phone numbers, email addresses, emergency contact details, and financial/billing information (e.g., credit card or EFT details).',
      },
      {
        label: 'Health Information (Governed by HIA):',
        text: "The Company collects, processes, and maintains highly sensitive personal information regarding clients' physical, medical, and cognitive status. This data is collected strictly for the purpose of administering contracted services.",
      },
    ],
  },
  {
    n: 3,
    h: 'How We Use Your Information',
    paras: [
      'True North Care and Support strictly limits the use of your information to necessary business and clinical functions, including:',
    ],
    bullets: [
      { text: 'Conducting home assessments and designing your Individualized Care Plan.' },
      { text: 'Scheduling caregivers and ensuring seamless delivery of home care services.' },
      {
        text: 'Processing bi-weekly invoices and coordinating direct billing with Alberta Blue Cross (via the CDHCI framework) and Medavie Blue Cross.',
      },
      {
        text: 'Reviewing quality of care, field competency audits, and managing internal safety incidents (such as falls or medication errors).',
      },
    ],
  },
  {
    n: 4,
    h: 'When and How We Disclose Your Information',
    paras: [
      'Your privacy is our priority. We never sell, rent, or trade your information. Your health and personal records will only be shared with third parties under the following specific conditions:',
    ],
    bullets: [
      {
        label: 'With Your Express Consent:',
        text: 'We will share information with family members or legal representatives (such as individuals holding a Personal Directive or Power of Attorney) as designated by you in writing.',
      },
      {
        label: 'Circle of Care Continuity:',
        text: 'We may share vital health information with your primary care physician, local pharmacists, or emergency medical services if required for your immediate medical well-being.',
      },
      {
        label: 'Provincial Funding Coordination:',
        text: 'We will securely transmit approved care-hour records and invoicing data to the applicable funding agency or claims administrator (for example, Alberta Blue Cross or Medavie Blue Cross), where necessary to administer approved funding.',
      },
      {
        label: 'Legal Mandates:',
        text: "We will disclose information without consent only when legally required by law, such as reporting suspected elder abuse under Alberta's Protection for Persons in Care Act (PPCA) or responding to a valid court subpoena.",
      },
    ],
  },
  {
    n: 5,
    h: 'Safeguards and Data Storage',
    paras: [
      'We implement rigorous physical, technical, and administrative security measures to ensure your data remains secure:',
    ],
    bullets: [
      {
        label: 'Digital Records:',
        text: 'All electronic health records, care plans, and scheduling logs are protected by industry-standard administrative, physical, and technical safeguards, including password-protected systems and access controls.',
      },
      {
        label: 'Mobile Workforce Rules:',
        text: 'Our caregivers are strictly prohibited from storing client medical charts on personal mobile devices, taking unauthorized photos inside client homes, or leaving physical records unattended.',
      },
      {
        label: 'Physical Records:',
        text: 'Any physical intake forms or signed contracts are held in locked filing cabinets restricted exclusively to authorized administrative staff.',
      },
      {
        label: 'Retention:',
        text: "Records will be retained for the period required by applicable legislation, contractual obligations, funding program requirements, and the Provider's records retention policies, after which they will be securely destroyed.",
      },
    ],
  },
  {
    n: 6,
    h: 'Mandatory Breach Notification',
    paras: [
      'In the highly unlikely event of a security breach involving your personal or health data (such as a lost work tablet or unauthorized system access) that creates a real risk of significant harm, True North Care and Support will:',
    ],
    steps: [
      'Immediately act to contain the breach and mitigate data exposure.',
      'Notify you or your legal representative directly without unreasonable delay.',
      'Formally report the incident to the Office of the Information and Privacy Commissioner (OIPC) of Alberta in accordance with legal provincial breach reporting mandates.',
    ],
  },
  {
    n: 7,
    h: 'Your Rights Regarding Your Information',
    paras: [
      'Under Alberta privacy legislation, you or your legally authorized representative maintains the right to:',
    ],
    bullets: [
      { text: 'Request access to review the personal and health records we maintain about you.' },
      { text: 'Request corrections to any inaccurate or incomplete information in your file.' },
      {
        text: 'Withdraw your consent for certain data collections or disclosures at any time (noting that withdrawing consent may limit our operational ability to safely deliver your care).',
      },
    ],
  },
  {
    n: 8,
    h: 'Changes to This Policy',
    paras: [
      'True North Care and Support may update this Privacy Policy from time to time to reflect changes in legislation, technology, or business practices. The most current version will be made available to clients upon request and on our website, if applicable.',
    ],
  },
]

const headingStyle = {
  margin: '0 0 12px 0',
  fontSize: 'clamp(1.35rem, 4.5vw, 1.75rem)',
  lineHeight: 1.2,
  color: 'var(--color-teal-900)',
}
const paraStyle = { margin: '0 0 16px 0', fontSize: 17, lineHeight: 1.7, color: 'var(--color-muted)' }
const listStyle = { margin: '0 0 16px 0', paddingLeft: 24, fontSize: 17, lineHeight: 1.7, color: 'var(--color-muted)' }

export default function Privacy() {
  return (
    <section className="section">
      <div className="container-site">
        <div style={{ maxWidth: 820 }}>
          <p className="eyebrow">Client Privacy Policy</p>
          <h1
            style={{
              margin: '12px 0 0 0',
              fontSize: 'clamp(2rem, 6vw, 2.9rem)',
              lineHeight: 1.15,
              color: 'var(--color-teal-900)',
            }}
          >
            Protecting your personal and health information
          </h1>
          <p style={{ margin: '12px 0 32px 0', fontSize: 15, color: 'var(--color-muted)' }}>Last updated: August 2026</p>

          <p style={{ ...paraStyle, fontSize: 19, marginBottom: 40 }}>
            At True North Care and Support, we are deeply committed to protecting the privacy, confidentiality, and
            security of our clients' personal and health information. This Privacy Policy outlines how we collect, use,
            disclose, store, and safeguard your data in strict compliance with Alberta's{' '}
            <strong style={{ color: 'var(--color-teal-900)' }}>Health Information Act (HIA)</strong> and the{' '}
            <strong style={{ color: 'var(--color-teal-900)' }}>Personal Information Protection Act (PIPA)</strong>.
          </p>

          {SECTIONS.map((s) => (
            <div key={s.n} style={{ marginBottom: 40, borderTop: '1px solid var(--color-line)', paddingTop: 32 }}>
              <h2 style={headingStyle}>
                {s.n}. {s.h}
              </h2>
              {s.paras.map((p) => (
                <p key={p} style={paraStyle}>
                  {p}
                </p>
              ))}
              {s.bullets && (
                <ul style={listStyle}>
                  {s.bullets.map((b) => (
                    <li key={b.text} style={{ marginBottom: 10 }}>
                      {b.label && <strong style={{ color: 'var(--color-teal-900)' }}>{b.label} </strong>}
                      {b.text}
                    </li>
                  ))}
                </ul>
              )}
              {s.steps && (
                <ol style={listStyle}>
                  {s.steps.map((t) => (
                    <li key={t} style={{ marginBottom: 10 }}>
                      {t}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          ))}

          {/* 9. Contact */}
          <div style={{ borderTop: '1px solid var(--color-line)', paddingTop: 32 }}>
            <h2 style={headingStyle}>9. Contact Information and Privacy Officer</h2>
            <p style={paraStyle}>
              If you have questions, concerns, or wish to exercise your privacy rights, please contact our designated
              Privacy Officer:
            </p>
            <div
              style={{
                borderRadius: 8,
                border: '1px solid var(--color-line)',
                background: 'var(--color-panel)',
                padding: 'clamp(20px, 5vw, 28px)',
                fontSize: 17,
                lineHeight: 1.7,
                color: 'var(--color-muted)',
                overflowWrap: 'anywhere',
              }}
            >
              <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-teal-900)' }}>True North Care and Support</p>
              <p style={{ margin: '8px 0 0 0' }}>
                <strong style={{ color: 'var(--color-teal-900)' }}>Attn:</strong> Jenish Patel, Director &amp; Privacy
                Officer
              </p>
              <p style={{ margin: '8px 0 0 0' }}>
                <strong style={{ color: 'var(--color-teal-900)' }}>Phone:</strong>{' '}
                <a href={PHONE_TEL} style={{ color: 'var(--color-teal-800)', fontWeight: 600 }}>
                  {PHONE_DISPLAY}
                </a>
              </p>
              <p style={{ margin: '8px 0 0 0' }}>
                <strong style={{ color: 'var(--color-teal-900)' }}>Mailing address:</strong> 210-4503 Brisebois Dr NW,
                Calgary, Alberta T2L 2G3
              </p>
            </div>
            <p style={{ ...paraStyle, marginTop: 24 }}>
              If we are unable to resolve your privacy concern to your satisfaction, you maintain the right to contact
              the provincial regulator directly:
            </p>
            <p style={{ ...paraStyle, marginBottom: 0 }}>
              <em>Office of the Information and Privacy Commissioner of Alberta (OIPC)</em>
              <br />
              <a
                href="https://www.oipc.ab.ca"
                target="_blank"
                rel="noreferrer"
                style={{ color: 'var(--color-teal-800)', fontWeight: 600 }}
              >
                www.oipc.ab.ca
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
